import { TextField } from "@codel/dova-ds";

interface VerifyCodeFieldsProps {
  code: string;
  onCodeChange: (code: string) => void;
  /** Message under the field. Absent when there is nothing wrong. */
  error?: string;
  /** The address the code went to, for the caption. Omitted when unknown. */
  email?: string | null;
  /** Locked out after too many attempts: the field goes dead with the primary. */
  locked?: boolean;
  /** Absent ⇒ no resend affordance is offered at all. */
  onResend?: () => void;
  resending?: boolean;
  resent?: boolean;
}

/** Digits only, six of them. Enforced here rather than in each caller, so the two
 *  hosts cannot drift on what the field accepts. */
const normalizeCode = (value: string) => value.replace(/\D/g, "").slice(0, 6);

/** The label both hosts put on the primary. Shared because the button is NOT: the
 *  two place it differently, and a constant is what stops the wording drifting the
 *  way the rest of this screen already had. */
export const VERIFY_CODE_SUBMIT_LABEL = "Verify email";
export const VERIFY_CODE_SUBMITTING_LABEL = "Verifying…";

/**
 * The body of the 6-digit code screen, rendered identically wherever a client is
 * asked for one.
 *
 * WHY IT EXISTS. Two places ask: step 4 of onboarding, and the signing surface when
 * a secure session has timed out and the same mailbox has to be proven again. They
 * were built separately and had drifted — two copy lines, two button labels, two
 * field treatments, and a resend link on only one, so a client whose second code
 * never arrived was stranded on the screen with no way to ask for another.
 * Identical BY CONSTRUCTION is the point; identical by two people remembering is
 * what already failed.
 *
 * WHAT IT DELIBERATELY DOES NOT RENDER: the `<form>` and the submit button. The
 * hosts disagree about both, and correctly. Onboarding's StepShell already owns a
 * form element and puts the primary in its footer beside Back; the signing surface
 * has no Back and carries its button inline. A component that rendered its own form
 * would nest one inside StepShell's, which is invalid and breaks Enter-to-submit.
 * So the BODY is shared and the chrome is each host's own — with the label above
 * shared as a constant, since that is the part that has no reason to differ.
 *
 * The field is large, centred and letter-spaced. A six-digit code is read back
 * character by character off another screen, and a default-sized field made that
 * harder than it needed to be.
 */
export function VerifyCodeFields({
  code,
  onCodeChange,
  error,
  email,
  locked = false,
  onResend,
  resending = false,
  resent = false,
}: VerifyCodeFieldsProps) {
  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="verify-code"
        label="6-digit code"
        value={code}
        onChange={(value) => onCodeChange(normalizeCode(value))}
        error={error || undefined}
        placeholder="123456"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        disabled={locked}
        autoFocus
        // WHICH MAILBOX TO OPEN, as the field's own hint rather than as a
        // paragraph beside it. It names the address instead of saying "your
        // email" because a client may have more than one, and on the signing
        // surface they confirmed this one several steps earlier.
        //
        // It was a sibling <p> hand-styled to --type-body-sm / --text-tertiary,
        // which is the DS helper treatment copied out by hand — so it also sat
        // in the wrapper's `gap-4` and took a full 16px of air, the same as the
        // resend link and the primary below it. Those are separate actions; this
        // is a caption ON the field, and it was reading as a peer of things it
        // is not. The helper slot spaces it at `mt-2` (8px) and, because it is
        // the slot wired to `aria-describedby`, the address is now announced
        // with the field rather than floating unlabelled beside it.
        //
        // TRADE: FormField shows an error INSTEAD of the helper, so a wrong code
        // hides the address until the field clears. That is the DS's call about
        // one message per field, and the more urgent message wins.
        hint={email ? `Code sent to ${email}.` : undefined}
        // `!` because TextInput's own text-base is the same specificity.
        //
        // The PLACEHOLDER opts out of the bold. Without that it inherits the whole
        // treatment — 28px, bold, letter-spaced — and `123456` sitting in an empty
        // field reads as a code someone has already typed rather than as a hint at
        // the shape of one. Weight, not a lighter colour: --text-tertiary is already
        // the lightest text that clears AA on this ground, and the step below it is
        // --text-disabled, which would say the field is dead.
        className="!text-[28px] text-center font-bold tracking-[0.35em] placeholder:font-normal"
      />

      {/* Offered only when the host can actually send another. Without it, a client
          whose code never arrives has no way forward from this screen.

          THE LABEL STAYS AN ACTION. It used to become "Sent. Check your inbox again."
          on success, which turned the only control on the screen into a sentence: an
          underlined status that still looked pressable but no longer said what
          pressing it would do, exactly when a client whose code had not arrived would
          be reaching for it a second time. The confirmation is a separate line now,
          and the button keeps its job. */}
      {onResend ? (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onResend}
            disabled={resending || locked}
            className="m-0 self-start bg-transparent p-0 text-[14px] underline text-[var(--text-secondary)] disabled:no-underline disabled:text-[var(--text-tertiary)]"
          >
            {resending ? "Sending…" : "Didn't get it? Send another code"}
          </button>
          {resent && !resending ? (
            <p role="status" className="[font:var(--type-body-sm)] text-[var(--text-tertiary)]">
              Sent. It can take a minute to arrive.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
